/// <reference lib="dom" />
import type { AssetSource, LoadedAsset, AssetPath } from './types.js';
import type { AssetAction, AssetChange } from './Asset.js';

/**
 * Path-keyed asset reader mirroring the real framework's AssetReader
 * (getFile / getJSON) with canvas-oriented helpers. ONE shared singleton:
 * sandbox client AND server systems import the same instance, so reads are
 * consistent "from both sides." Game code uses the sync getters in the render
 * loop — safe because the host calls __init() at boot, preloading everything.
 */
export class BaseAssetManager {
  protected cache = new Map<string, LoadedAsset>();
  private accessListener: ((path: string) => void) | null = null;
  private source?: AssetSource;
  private pending = new Map<string, Promise<LoadedAsset>>();
  private changeListeners = new Set<(action: AssetAction, change: AssetChange) => void>();

  constructor(source?: AssetSource) {
    this.source = source;
  }

  /** Host-only: inject the byte source and preload all assets. */
  async __init(source: AssetSource): Promise<void> {
    this.source = source;
    this.clearCache();
    await Promise.all(
      source.manifest().map(async (e) => {
        this.cache.set(e.path, await source.load(e.path));
      }),
    );
  }

  /**
   * Fetch a path through the source with in-flight coalescing: concurrent
   * callers share one request, and the cache-set is guarded to the current
   * pending owner, so a stale fetch — superseded by a newer fetch, or by
   * applyRemoteChange dropping the pending entry — cannot clobber fresher
   * cache state.
   */
  private fetchAndCache(fullPath: string): Promise<LoadedAsset> {
    const inflight = this.pending.get(fullPath);
    if (inflight) return inflight;
    const source = this.source!;
    const p = source.load(fullPath).then((loaded) => {
      if (this.pending.get(fullPath) === p) this.cache.set(fullPath, loaded);
      return loaded;
    }).finally(() => {
      if (this.pending.get(fullPath) === p) this.pending.delete(fullPath);
    });
    this.pending.set(fullPath, p);
    return p;
  }

  /**
   * On-demand load via the injected source.
   * - Cache hit → the cached asset.
   * - Concurrent misses for the same path share ONE source request (coalesced):
   *   the second caller gets the first's pending promise.
   * - `cache:false` → an independent fresh read, neither served from nor written
   *   to the cache and not joined to any in-flight request.
   * The cache write is guarded to the current pending owner so a stale in-flight
   * read (invalidated by a change mid-flight) cannot clobber fresher data.
   */
  async loadRaw(fullPath: string, opts?: { cache?: boolean }): Promise<LoadedAsset> {
    if (!this.source) {
      throw new Error(`BaseAssetManager: no source configured; cannot load "${fullPath}".`);
    }
    if (opts?.cache === false) {
      return this.source.load(fullPath);
    }
    const cached = this.cache.get(fullPath);
    if (cached) return cached;
    return this.fetchAndCache(fullPath);
  }

  /** Subscribe to applied changes (fired AFTER the internal cache is coherent). */
  onChange(cb: (action: AssetAction, change: AssetChange) => void): () => void {
    this.changeListeners.add(cb);
    return () => this.changeListeners.delete(cb);
  }

  protected emit(action: AssetAction, change: AssetChange): void {
    for (const l of this.changeListeners) l(action, change);
  }

  /**
   * Apply a change that happened elsewhere in the cluster: internal cache work
   * FIRST (reload only what was loaded / evict), THEN the listeners. Drops any
   * in-flight fetch for the path so a stale read cannot repopulate the cache.
   */
  async applyRemoteChange(action: AssetAction, change: AssetChange): Promise<void> {
    const full = `${change.namespace}/${change.path}`;
    this.pending.delete(full);
    if (action === 'updated') {
      if (this.cache.has(full) && this.source) {
        // Eager reload, but ownership-guarded (a racing change/load wins) and
        // non-rejecting (a reload of an already-removed asset just evicts).
        await this.fetchAndCache(full).catch(() => { this.cache.delete(full); });
      }
    } else {
      this.cache.delete(full);
    }
    this.emit(action, change);
  }

  protected cacheSet(path: string, loaded: LoadedAsset): void {
    this.cache.set(path, loaded);
  }
  protected cacheDelete(path: string): void {
    this.cache.delete(path);
  }

  /** Host-only: drop all state (called before a re-run). */
  __reset(): void {
    this.clearCache();
    this.accessListener = null;
  }

  /**
   * Host-only: observe every successful read (getImage/getJSON/getFile/getUrl).
   * The sandbox test runner uses this to log which assets game code actually
   * touched so asset assertions can diagnose "loaded but never read" precisely.
   * Cleared by __reset() so a leftover listener can never leak into a live run.
   */
  __setAccessListener(cb: ((path: string) => void) | null): void {
    this.accessListener = cb;
  }

  /**
   * Clear the cache, revoking any blob/object URLs first so repeated
   * __init/__reset cycles don't leak them. Guarded so core stays usable in
   * non-DOM hosts (Node), where URL.revokeObjectURL doesn't exist.
   */
  private clearCache(): void {
    if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
      for (const a of this.cache.values()) {
        if (a.url) URL.revokeObjectURL(a.url);
      }
    }
    this.cache.clear();
  }

  private require(path: string): LoadedAsset {
    const a = this.cache.get(path);
    if (!a) {
      throw new Error(
        `AssetManager: no asset at "${path}". Known: ${[...this.cache.keys()].join(', ') || '(none)'}`,
      );
    }
    if (this.accessListener) {
      try { this.accessListener(path); } catch { /* a listener must never break game reads */ }
    }
    return a;
  }

  has(path: AssetPath): boolean {
    return this.cache.has(path as string);
  }

  /** All asset paths, optionally filtered to a folder prefix (mirrors AssetReader dir listing). */
  list(folder?: string): string[] {
    const all = [...this.cache.keys()];
    if (!folder) return all;
    const prefix = folder.endsWith('/') ? folder : `${folder}/`;
    return all.filter((p) => p.startsWith(prefix));
  }

  /** Decoded image, ready for ctx.drawImage. Throws if missing or not a decoded image. */
  getImage(path: AssetPath): HTMLImageElement {
    const a = this.require(path as string);
    if (!a.image) throw new Error(`AssetManager: asset "${path}" is not a decoded image (mime ${a.mimeType}).`);
    return a.image as HTMLImageElement;
  }

  /** Parsed JSON (mirrors AssetReader.getJSON). */
  getJSON<T = unknown>(path: AssetPath): T {
    return JSON.parse(new TextDecoder().decode(this.require(path as string).bytes)) as T;
  }

  /** Raw bytes (mirrors AssetReader.getFile). */
  getFile(path: AssetPath): Uint8Array {
    return this.require(path as string).bytes;
  }

  /** Blob/object URL for the asset (browser). */
  getUrl(path: AssetPath): string {
    const a = this.require(path as string);
    if (!a.url) throw new Error(`AssetManager: asset "${path}" has no URL.`);
    return a.url;
  }

  /**
   * Synchronous cache read for the sync `$assets` getters. Unlike the private
   * `require` (used by the legacy global-singleton getters), this throws the
   * preload-nudge error, steering the caller to declare a preload rather than
   * await. Fires the access listener on a hit (it IS a read), so the sandbox's
   * "touched" probes see sync-getter reads.
   */
  peek(fullPath: string): LoadedAsset {
    const a = this.cache.get(fullPath);
    if (!a) {
      throw new Error(
        `Asset "${fullPath}" is not loaded — sync getters only read preloaded assets. ` +
        `Add it to the area/game file's 'preloads', check its Preload box in the sandbox ` +
        `assets panel, or 'await $assets.<ns>.load(...)' it before reading synchronously.`,
      );
    }
    if (this.accessListener) {
      try { this.accessListener(fullPath); } catch { /* a listener must never break reads */ }
    }
    return a;
  }

  /** Non-throwing cache presence check (backs the sync `has`). */
  hasCached(fullPath: string): boolean {
    return this.cache.has(fullPath);
  }
}

export type AssetManager = BaseAssetManager;
export const AssetManager = new BaseAssetManager();
export default AssetManager;
